import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { I18NextPipe } from 'angular-i18next';
import { AuthService } from 'app/core/auth/auth.service';
import { CONFIG } from 'app/core/config';
import { FeatureService } from 'app/features/feature.service';
import { NotificationService } from 'app/notifications/notification.service';
import { UserPermissionKeys } from 'app/security/authorization/user-permission-keys';
import { UserlaneService } from 'app/thirdparty/userlane/userlane.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PermissionSet } from '../../core/auth/permission-set';
import { ConfigService } from '../../core/config.service';
import { PermissionsService } from '../../core/permissions.service';
import { EventParameters } from '../../events/event-parameters';
import { EventsService } from '../../events/events.service';


@Component({
  selector: 'hb-header',
  templateUrl: './app-header.component.html',
  styleUrls: ['./app-header.component.scss'],
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  public userAuthenticated: boolean;
  public loggedInUser: string;
  public bellIcon: string = 'far fa-bell';
  public notificationsCount: number = 0;
  public eventCount: number = 0;
  public eventsManagementEnabled: boolean = false;

  // Whether Userlane is enabled via the feature flags
  public userlaneEnabled: boolean = false;

  // Whether or nor we want to show Userlane right now
  public userlaneShown: boolean;

  private hasEventsReadPermission: boolean = false;
  private hasAdminAccess: boolean = false;

  private unsubscribe: Subject<boolean> = new Subject();
  private userPermissionKeys: UserPermissionKeys = new UserPermissionKeys();


  private catalog;

  constructor(
    private el: ElementRef,
    private router: Router,
    private loginService: AuthService,
    private notificationService: NotificationService,
    private eventsService: EventsService,
    private permissionsService: PermissionsService,
    private featureService: FeatureService,
    private userlaneService: UserlaneService,
    private i18NextPipe: I18NextPipe,
    private configService: ConfigService,

  ) { }

  public ngOnInit(): void {

    this.configService.selectedCatalog.pipe(takeUntil(this.unsubscribe)).subscribe(value => {
      this.catalog = value;
      this.subscribeToPermissionChanges();
      this.subscribeToLoginService();
    });
    this.rearrangeMarkupElements();
    this.setNotificationCount();
    this.assignUserlaneData();

  }

  public ngOnDestroy(): void {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  private assignUserlaneData(): void {
    this.userlaneService.userlaneToggle.pipe(takeUntil(this.unsubscribe)).subscribe(shown => {
      this.userlaneShown = shown;
    });

    this.userlaneService.userlaneToggle.next(this.userlaneService.userLaneShown());
  }

  private setEventsManagementEnabling(): void {
    this.eventsManagementEnabled = this.hasEventsReadPermission || this.hasAdminAccess;
  }

  private subscribeToLoginService(): void {
    this.loginService.loggedIn.pipe(takeUntil(this.unsubscribe)).subscribe(isAuthenticated => {
      this.userAuthenticated = isAuthenticated;

      if (this.userAuthenticated && this.eventsManagementEnabled) {
        this.setupUserEventCount();
      }
    });

    this.loginService.loggedInUser.pipe(takeUntil(this.unsubscribe)).subscribe(data => {
      this.loggedInUser = data;
    });
  }

  public logout(): void {
    this.loginService.logout();
  }

  public toggleUserlaneClicked(): void {
    this.userlaneService.toggleUserlane(!this.userlaneShown);
  }

  private rearrangeMarkupElements(): void {
    const nativeElement: HTMLElement = this.el.nativeElement;
    const parentElement: HTMLElement = nativeElement.parentElement;

    // move all children out of the element
    while (nativeElement.firstChild) {
      parentElement.insertBefore(nativeElement.firstChild, nativeElement);
    }

    // remove the empty element(the host)
    parentElement.removeChild(nativeElement);
  }

  private subscribeToPermissionChanges(): void {
    this.checkEventsLicense(this.permissionsService.Permissions);

    this.permissionsService.userPermissionKeyValuePairs.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.hasEventsReadPermission = this.permissionsService.checkUserHasPermissions(this.userPermissionKeys.eventsEventItemRead);
      this.setEventsManagementEnabling();
    });

    this.permissionsService.PermissionsChangedEvent.pipe(takeUntil(this.unsubscribe)).subscribe((permissions: PermissionSet) => {
      this.checkEventsLicense(permissions);
    });
  }

  public getDashboardLink(){
    return `#/${this.configService.selectedCatalog.getValue()}/dashboard`;
  }

  private checkEventsLicense(permissions: PermissionSet): void {
    this.hasAdminAccess = permissions.accessAdmin;
    this.setEventsManagementEnabling();
  };

  private setNotificationCount(): void {
    this.notificationService.notifications.pipe(takeUntil(this.unsubscribe)).subscribe(notifications => {
      if (notifications) {
        this.notificationsCount = notifications.length;
      }
    });
  }

  private setupUserEventCount(): void {
    this.getUserEventCount();
    this.eventsService.eventsChanged.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.getUserEventCount());
  }

  private getUserEventCount(): void {
    const parameters = new EventParameters();

    parameters.onlyUser = true;
    parameters.onlyActive = true;

    this.eventsService.getUserEventCount(parameters).pipe(takeUntil(this.unsubscribe)).subscribe(count => {
      this.eventCount = count;
    });
  }

  public redirectToEvents() {
    return this.loginService.urlCatalog + CONFIG.routes.events;


  }
}
